import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight, Building2, UtensilsCrossed, Scissors, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Hero use-case slideshow.
 * - 4 slides cycle every 5s
 * - Manual arrows + dot pagination
 * - Touch / swipe support on mobile
 * - Pauses auto-advance on hover / touch interaction
 * - Optimized images served from Unsplash CDN (w=1600,q=75)
 */
const SLIDES = [
  {
    id: "apartments",
    eyebrow: "For Property Managers",
    title: "Apartment Parking Made Simple",
    body: "Tag every resident vehicle with a unique QR sticker. One scan tells your team whether the car belongs, who it belongs to, and which unit it's tied to.",
    cta: "Start for $99",
    ctaTo: "/register",
    secondaryCta: { label: "See Pricing", href: "#pricing" },
    image:
      "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1600&q=70",
    imageAlt: "Modern apartment complex with parking lot",
    Icon: Building2,
    accent: "from-[#0F8F4F]/80 via-[#0F8F4F]/40 to-transparent",
  },
  {
    id: "restaurants",
    eyebrow: "For Restaurants",
    title: "Manage Restaurant Parking",
    body: "Issue dynamic QR passes for diners, staff, and reserved spots. Cut down on tow disputes and free up your busiest lots when you need them most.",
    cta: "Start for $99",
    ctaTo: "/register",
    secondaryCta: { label: "See Pricing", href: "#pricing" },
    image:
      "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1600&q=70",
    imageAlt: "Restaurant dining room ready for guests",
    Icon: UtensilsCrossed,
    accent: "from-[#B6471A]/80 via-[#B6471A]/30 to-transparent",
  },
  {
    id: "barbers",
    eyebrow: "For Barbershops",
    title: "Showcase Your Work",
    body: "Stick a QR on the mirror. Clients scan and instantly see your haircut portfolio, prices, and booking link. Coming soon to ScanMint.",
    cta: "Join the waitlist",
    ctaTo: "/register",
    secondaryCta: { label: "See Pricing", href: "#pricing" },
    image:
      "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?auto=format&fit=crop&w=1600&q=70",
    imageAlt: "Inside a classic barbershop with chairs and mirrors",
    Icon: Scissors,
    accent: "from-[#1F3A8A]/80 via-[#1F3A8A]/30 to-transparent",
  },
  {
    id: "private",
    eyebrow: "For Private Use",
    title: "Flexible QR System",
    body: "Verify equipment, badges, deliveries, or any real-world asset. One platform, unlimited objects, every scan logged with a timestamp.",
    cta: "Start for $99",
    ctaTo: "/register",
    secondaryCta: { label: "See Pricing", href: "#pricing" },
    image:
      "https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?auto=format&fit=crop&w=1600&q=70",
    imageAlt: "Scanning a QR code with a phone",
    Icon: ShieldCheck,
    accent: "from-[#5B21B6]/80 via-[#5B21B6]/30 to-transparent",
  },
];

const AUTO_ADVANCE_MS = 5000;
const SWIPE_THRESHOLD = 50;

export default function UseCaseSlideshow() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const total = SLIDES.length;

  const goTo = useCallback((i) => {
    setIndex(((i % total) + total) % total);
  }, [total]);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  // Auto-advance
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(t);
  }, [paused, total]);

  // Preload neighbouring images so the next slide arrives instantly.
  useEffect(() => {
    const ids = [(index + 1) % total, (index - 1 + total) % total];
    ids.forEach((i) => {
      const img = new Image();
      img.src = SLIDES[i].image;
    });
  }, [index, total]);

  // Keyboard nav
  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [next, prev]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    setPaused(true);
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) {
      setPaused(false);
      return;
    }
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > SWIPE_THRESHOLD) prev();
    else if (dx < -SWIPE_THRESHOLD) next();
    touchStartX.current = null;
    // Resume auto-advance after a brief pause
    setTimeout(() => setPaused(false), 1500);
  };

  const slide = SLIDES[index];
  const Icon = slide.Icon;

  return (
    <section
      className="relative bg-[#09090B] text-white overflow-hidden"
      data-testid="usecase-slideshow"
      aria-roledescription="carousel"
      aria-label="ScanMint use cases"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Stacked slides - opacity transition for cross-fade */}
      <div className="relative w-full h-[520px] sm:h-[560px] md:h-[600px]">
        {SLIDES.map((s, i) => (
          <div
            key={s.id}
            className={`absolute inset-0 transition-opacity duration-700 ease-out ${
              i === index ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            aria-hidden={i !== index}
            data-testid={`usecase-slide-${s.id}`}
          >
            {/* Image layer */}
            <img
              src={s.image}
              alt={s.imageAlt}
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
              fetchPriority={i === 0 ? "high" : "low"}
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />
            {/* Dark gradient overlay so text remains legible */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
            {/* Brand accent wash */}
            <div className={`absolute inset-0 bg-gradient-to-tr ${s.accent} mix-blend-multiply opacity-60`} />
          </div>
        ))}

        {/* Content overlay — single block, content swaps based on index */}
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 flex items-center">
          <div className="max-w-2xl">
            <div
              className="flex items-center gap-2 mono-tiny text-[#0F8F4F]"
              data-testid={`usecase-eyebrow-${slide.id}`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={2} />
              {slide.eyebrow}
            </div>
            <h2
              key={`title-${slide.id}`}
              className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.04] animate-[fadeUp_0.6s_ease-out]"
              data-testid={`usecase-title-${slide.id}`}
            >
              {slide.title}
            </h2>
            <p
              key={`body-${slide.id}`}
              className="mt-5 text-base md:text-lg text-white/80 max-w-xl leading-relaxed animate-[fadeUp_0.7s_ease-out]"
            >
              {slide.body}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to={slide.ctaTo} data-testid={`usecase-cta-${slide.id}`}>
                <Button className="bg-[#0F8F4F] hover:bg-[#0A6B3B] h-12 px-6 text-base">
                  {slide.cta} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              {slide.secondaryCta && (
                <a href={slide.secondaryCta.href} data-testid={`usecase-secondary-${slide.id}`}>
                  <Button
                    variant="outline"
                    className="h-12 px-6 text-base bg-white/5 border-white/30 text-white hover:bg-white/15 hover:text-white"
                  >
                    {slide.secondaryCta.label}
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Arrows */}
        <button
          type="button"
          onClick={prev}
          aria-label="Previous slide"
          data-testid="usecase-arrow-prev"
          className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-white/10 backdrop-blur border border-white/20 text-white hover:bg-white/20 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="Next slide"
          data-testid="usecase-arrow-next"
          className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-white/10 backdrop-blur border border-white/20 text-white hover:bg-white/20 transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Dots */}
        <div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2"
          data-testid="usecase-dots"
        >
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === index ? "true" : undefined}
              data-testid={`usecase-dot-${s.id}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index ? "w-8 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
